/**
 * CURATION-D1 — pure helpers for Home collection folders.
 */

import type { HubCollection, HubStyleCard } from "@/lib/fetchStyles";

export type HomeFolder = {
  id: string;
  label: string;
  season: string;
  count: number;
  thumbs: string[];
  plusN: number;
};

/** Drop styles with hidden === true; keep collection shape. */
export function excludeHiddenStyles(
  collections: HubCollection[] | null | undefined,
): HubCollection[] {
  return (collections ?? []).map((c) => ({
    ...c,
    styles: (c.styles ?? []).filter((s) => s.hidden !== true),
  }));
}

export function folderLabel(c: HubCollection): string {
  if (c.untitled) return "Untitled";
  return (c.name ?? "Untitled").trim() || "Untitled";
}

export function folderSeason(c: HubCollection): string {
  const parts = [
    c.season,
    c.quarter,
    c.year != null ? String(c.year) : null,
  ].filter((p): p is string => typeof p === "string" && p.trim().length > 0);
  return parts.join(" ");
}

export function sortStylesByActivity(styles: HubStyleCard[]): HubStyleCard[] {
  return [...styles].sort((a, b) => {
    const at = a.last_activity_at
      ? new Date(a.last_activity_at).getTime()
      : null;
    const bt = b.last_activity_at
      ? new Date(b.last_activity_at).getTime()
      : null;
    if (at == null && bt != null) return 1;
    if (at != null && bt == null) return -1;
    if (at != null && bt != null && at !== bt) return bt - at;
    const an = a.style_number;
    const bn = b.style_number;
    if (an == null && bn != null) return 1;
    if (an != null && bn == null) return -1;
    if (an != null && bn != null && an !== bn) return an - bn;
    return (a.name ?? "").localeCompare(b.name ?? "");
  });
}

/** CURATION-D1-FB-1 — optimistic local patches applied on top of hubStyles. */
export type HomeStyleOverride = {
  collection_id?: string;
  hidden?: boolean;
  name?: string;
};

/**
 * Apply name / hidden / collection_id overrides. Moves styles between
 * collections when collection_id differs; preserves collection order.
 */
export function applyHomeStyleOverrides(
  collections: HubCollection[],
  overrides: ReadonlyMap<string, HomeStyleOverride>,
): HubCollection[] {
  if (!overrides.size) return collections;

  type Entry = { style: HubStyleCard; homeId: string };
  const entries: Entry[] = [];
  for (const c of collections) {
    for (const s of c.styles ?? []) {
      const ov = overrides.get(s.id);
      if (!ov) {
        entries.push({ style: s, homeId: c.id });
        continue;
      }
      const style: HubStyleCard = { ...s };
      if (ov.name !== undefined) style.name = ov.name;
      if (ov.hidden !== undefined) style.hidden = ov.hidden;
      let homeId = c.id;
      if (ov.collection_id !== undefined) {
        homeId = ov.collection_id;
        style.collection_id = ov.collection_id;
        const dest = collections.find((x) => x.id === ov.collection_id);
        if (dest) style.collection_name = dest.name;
      }
      entries.push({ style, homeId });
    }
  }

  return collections.map((c) => ({
    ...c,
    styles: entries.filter((e) => e.homeId === c.id).map((e) => e.style),
  }));
}

/**
 * Drop overrides whose fields already match the refreshed collections.
 * Unmatched / still-diverging overrides are kept.
 */
export function pruneSettledOverrides(
  collections: HubCollection[],
  overrides: ReadonlyMap<string, HomeStyleOverride>,
): Map<string, HomeStyleOverride> {
  const next = new Map<string, HomeStyleOverride>();
  if (!overrides.size) return next;

  const byId = new Map<string, { style: HubStyleCard; collectionId: string }>();
  for (const c of collections) {
    for (const s of c.styles ?? []) {
      byId.set(s.id, { style: s, collectionId: c.id });
    }
  }

  for (const [id, ov] of overrides) {
    const found = byId.get(id);
    if (!found) {
      next.set(id, ov);
      continue;
    }
    let settled = true;
    if (
      ov.collection_id !== undefined &&
      found.collectionId !== ov.collection_id
    ) {
      settled = false;
    }
    if (
      ov.hidden !== undefined &&
      (found.style.hidden === true) !== (ov.hidden === true)
    ) {
      settled = false;
    }
    if (
      ov.name !== undefined &&
      (found.style.name?.trim() ?? "") !== ov.name.trim()
    ) {
      settled = false;
    }
    if (!settled) next.set(id, ov);
  }
  return next;
}

/**
 * CURATION-E1b-1 — global Home search over collection display names + style names.
 * Hidden styles are never returned. Empty / whitespace query → empty results.
 */
export function searchHome(
  collections: HubCollection[] | null | undefined,
  q: string,
): { collections: HubCollection[]; styles: HubStyleCard[] } {
  const query = q.trim().toLowerCase();
  if (!query) return { collections: [], styles: [] };

  const list = collections ?? [];
  const matchedCollections = list.filter((c) =>
    folderLabel(c).toLowerCase().includes(query),
  );

  const matchedStyles: HubStyleCard[] = [];
  for (const c of list) {
    for (const s of c.styles ?? []) {
      if (s.hidden === true) continue;
      const name = (s.name ?? "").toLowerCase();
      if (name.includes(query)) matchedStyles.push(s);
    }
  }

  return {
    collections: matchedCollections,
    styles: sortStylesByActivity(matchedStyles),
  };
}

/**
 * One folder per collection (including empty). Untitled collections first,
 * then preserve API order among the rest.
 */
export function buildFolders(collections: HubCollection[]): HomeFolder[] {
  const list = collections ?? [];
  const untitled = list.filter((c) => !!c.untitled);
  const rest = list.filter((c) => !c.untitled);
  const ordered = [...untitled, ...rest];

  return ordered.map((c) => {
    const visible = (c.styles ?? []).filter((s) => s.hidden !== true);
    const sorted = sortStylesByActivity(visible);
    const count = sorted.length;
    const thumbs = sorted
      .map((s) => s.thumb_url)
      .filter((u): u is string => typeof u === "string" && u.trim().length > 0)
      .slice(0, 4);
    const plusN = count > 4 ? count - 3 : 0;
    return {
      id: c.id,
      label: folderLabel(c),
      season: folderSeason(c),
      count,
      thumbs,
      plusN,
    };
  });
}
